/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useAuth0 } from '@auth0/auth0-react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Cog,
  CreditCard,
  DotsThreePlus,
  PageOverview,
  QuestionCircle,
  RM2,
  Sidebar as SidebarIcon,
} from '@remarkable/ark-icons';
// eslint-disable-next-line no-restricted-imports
import { Button } from '@remarkable/ark-web';
import { useLocation } from '@tanstack/react-router';
import clsx from 'clsx';
import { useEffect, useMemo } from 'react';
import { useAccessTokenClaims } from 'src/api/queries/user';
import {
  SidebarDivider,
  SidebarExternalLink,
  SidebarLabel,
  SidebarLink,
} from 'src/components';

import { defaultConfig } from 'src/config';
import { useSideMenuState } from 'src/hooks/useSideMenuState';
import { EXTERNAL_URLS } from 'src/utils/urls/externalUrls';
import { SUPPORT_URL } from 'src/utils/urls/supportUrls';
import { useFeatureFlags } from 'src/utils/useFeatureFlags';
import { useGlobalScrollLock } from 'src/utils/useGlobalScrollLock';
import { userHelpers } from 'src/utils/userHelpers';
import { useScreenWidth } from 'src/utils/useScreenWidth';

export function Sidebar() {
  const { t } = useLingui();
  const sideMenu = useSideMenuState();
  const pageWidth = useScreenWidth();
  const featureFlags = useFeatureFlags();
  const accessTokenClaims = useAccessTokenClaims();
  const auth = useAuth0();
  const location = useLocation();
  const isMobile = pageWidth < 640;
  const isDefaultOpen = useMemo(() => {
    // On larger screens, the sidebar should be open by default. On smaller screens, it should be closed.
    return pageWidth >= 1024;
  }, [pageWidth]);

  const isOpen = sideMenu.isOpen;

  useEffect(() => {
    if (!isDefaultOpen) {
      sideMenu.close();
    } else {
      sideMenu.open();
    }
  }, [isDefaultOpen]);

  useEffect(() => {
    // Close the sidebar when the route changes on smaller screens
    if (!isDefaultOpen) {
      sideMenu.close();
    }
  }, [location.pathname]);

  useGlobalScrollLock(isOpen && isMobile);

  if (!auth.user || userHelpers.isEmailVerified(auth.user) === false) {
    return null;
  }

  const canSeeEnterpriseLinks =
    userHelpers.isLoggedInToOrganization(auth.user) &&
    userHelpers.hasPermissionReadMembers(accessTokenClaims.data);

  return (
    <>
      <div
        // Purely a click-catching scrim. It is hidden from assistive tech
        // because closing the menu is already reachable from the menu itself.
        aria-hidden="true"
        onClick={() => {
          sideMenu.close();
        }}
        className={clsx(
          'fixed top-0 left-0 z-30 h-full w-full bg-black/50 transition-all lm:hidden',
          isOpen ? 'block opacity-100' : 'hidden opacity-0'
        )}
      ></div>
      <div
        className={clsx(
          'fixed top-0 z-30 h-screen shrink-0 flex-col items-start justify-start overflow-x-hidden overflow-y-scroll border-r border-paper-8 bg-paper-2 px-8 py-8 transition-[width,opacity] lm:sticky lm:z-40 lm:flex lm:overflow-y-clip',
          {
            'w-[240px] opacity-100': isOpen,
            'w-[64px] opacity-0 lm:opacity-100': !isOpen,
          }
        )}
      >
        <div className="hidden w-full justify-end lm:flex">
          <Button
            variant="tertiary-neutral"
            size="large"
            onClick={() => sideMenu.toggle()}
            className="!rounded"
            aria-label={isOpen ? t`Collapse sidebar` : t`Expand sidebar`}
          >
            <SidebarIcon className="!h-20" />
          </Button>
        </div>

        <nav
          className="mt-64 flex w-full flex-col gap-4 lm:mt-16"
          data-cy="sidebar-nav"
        >
          <SidebarLink
            to="/"
            icon={PageOverview}
            label={<Trans>Home</Trans>}
            expanded={isOpen}
          />
          <SidebarLink
            to="/device"
            icon={RM2}
            label={<Trans>Devices and apps</Trans>}
            expanded={isOpen}
          />{' '}
          <SidebarLink
            to="/integrations"
            icon={DotsThreePlus}
            label={<Trans>Integrations</Trans>}
            expanded={isOpen}
          />
          <SidebarLink
            to="/subscriptions"
            icon={CreditCard}
            label={<Trans>Subscription</Trans>}
            expanded={isOpen}
          />
          <SidebarLink
            to="/account"
            icon={Cog}
            label={<Trans>Account</Trans>}
            expanded={isOpen}
          />
          {canSeeEnterpriseLinks && (
            <>
              <SidebarDivider expanded={isOpen} />
              <SidebarLabel expanded={isOpen}>
                <Trans>Connect for Business</Trans>
              </SidebarLabel>

              <SidebarLink
                to="/enterprise"
                label={<Trans>Admin dashboard</Trans>}
                expanded={isOpen}
                openInNewTab={featureFlags.EnableEnterpriseInNewTab}
                showArrow
              />
            </>
          )}
          <SidebarDivider expanded={isOpen} />
          <SidebarLabel expanded={isOpen}>
            <Trans>Methods</Trans>
          </SidebarLabel>
          <SidebarExternalLink
            href={defaultConfig.MethodsUrl}
            label={<Trans>Templates</Trans>}
            expanded={isOpen}
          />
        </nav>
        <div className="flex w-full flex-1 flex-col justify-end">
          <SidebarDivider expanded={isOpen} />
          <SidebarLabel expanded={isOpen}>
            <Trans>More</Trans>
          </SidebarLabel>
          <SidebarExternalLink
            href={EXTERNAL_URLS.WEBAPP_HOME}
            label={<Trans>Web app</Trans>}
            expanded={isOpen}
          />
          <SidebarExternalLink
            iconOnExpanded={false}
            icon={QuestionCircle}
            href={SUPPORT_URL.HOME}
            label={<Trans>Support and FAQ</Trans>}
            expanded={isOpen}
          />
        </div>
      </div>
    </>
  );
}
